import {
  type SendData,
  type CollabStateBridge,
} from '@atlassian/collab-connection-client';
import { uuidv4 } from 'lib0/random.js';

import { encodeMessageV2 } from '@canvas/protocol/v2';
import { MessageType } from '@canvas/protocol-types';
import { getUpdateMessageOrigin } from '@canvas/transaction';

import { mergeUpdate } from '../util/merge-update';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates';
import { debugLog, debugWarn } from './debug-logger';
import { type UpdateMetadata } from './yjs-socket';

export class StateBridge
  implements
    CollabStateBridge<Uint8Array>,
    AbstractBatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>
{
  // stores all pending changes
  private buffer: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>[] = [];
  // stores the current pending outgoing updates
  private outgoing = 0;
  private outgoingId?: string;
  private observer?: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void;
  private flushObserver?: () => void;
  private confirmObserver?: (sentAt: number, receivedAt: number) => void;
  private mismatchObserver?: () => void;
  private lastSentClock: number | null = null;
  private retrievedAt: number | null = null;

  addUpdate(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    const bufferSizeBefore = this.buffer.length;
    this.buffer.push(update);
    debugLog(
      '[StateBridge:addUpdate] buffer size before=%d → after=%d, payload bytes=%d, clock=%d, hasObserver=%s',
      bufferSizeBefore,
      this.buffer.length,
      update.payload.byteLength,
      update.clock,
      Boolean(this.observer)
    );
    // calling observer here eventually triggers sendThenConfirm();
    this.observer?.(update);
  }

  flush() {
    if (this.buffer.length > 0) {
      this.flushObserver?.();
    }
  }

  // this will set observer to be a function that eventually calls c3.sendThenConfirm (through the abstract Transport layer)
  onUpdate(
    observer: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void
  ): this {
    this.observer = observer;
    return this;
  }

  offUpdate(): this {
    this.observer = undefined;
    return this;
  }

  onFlush(flushObserver: () => void): this {
    this.flushObserver = flushObserver;
    return this;
  }

  // no-op, timer is managed by C3, not by state bridge
  destroy() {}

  reset() {
    this.buffer = [];
    this.outgoing = 0;
    this.outgoingId = undefined;
    this.lastSentClock = null;
    this.retrievedAt = null;
  }

  /**
   * Resets the buffer and silently loads a single update without firing the
   * observer. Use this when the caller wants to populate the buffer for the
   * next `retrieve()` call (e.g. from `transport.send()`) without triggering
   * an immediate `sendThenConfirm` via the observer chain.
   */
  resetAndLoad(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    const bufferSizeBefore = this.buffer.length;
    this.reset();
    this.buffer.push(update);
    debugLog(
      '[StateBridge:resetAndLoad] cleared %d existing buffer item(s), loaded new update: payload bytes=%d, clock=%d',
      bufferSizeBefore,
      update.payload.byteLength,
      update.clock
    );
  }

  retrieve(): SendData<Uint8Array> | null {
    // return undefined payload if empty buffer
    if (this.buffer.length < 1) {
      debugLog(
        '[StateBridge:retrieve] buffer is empty — returning null (nothing to send)'
      );
      return null;
    }
    debugLog(
      '[StateBridge:retrieve] merging %d buffer item(s) to send. Individual payload sizes: [%s]',
      this.buffer.length,
      this.buffer.map((u) => u.payload.byteLength).join(', ')
    );
    const updatesToSend = this.buffer.map((update) => ({
      ...update,
      payload: structuredClone(update.payload),
    }));
    this.outgoing = updatesToSend.length;
    this.outgoingId = uuidv4();
    const mergedUpdate = mergeUpdate(updatesToSend);
    const updateToSend = {
      name: 'update',
      payload: encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: mergedUpdate.payload,
          origin: getUpdateMessageOrigin(mergedUpdate.origin),
          previousYjsClock: this.lastSentClock,
          currentYjsClock: mergedUpdate.clock,
        },
      }),
      id: this.outgoingId,
    };
    this.lastSentClock = mergedUpdate.clock;
    this.retrievedAt = Date.now();
    return updateToSend;
  }

  confirm(_confirmed: SendData<Uint8Array>, id?: string): Promise<void> {
    // check the id to make sure it matches
    if (id !== undefined && id === this.outgoingId) {
      debugLog(
        '[StateBridge:confirm] ✅ id matched (%s), removing %d outgoing item(s), remaining buffer: %d',
        id,
        this.outgoing,
        this.buffer.length - this.outgoing
      );
      this.retrievedAt && this.confirmObserver?.(this.retrievedAt, Date.now());
      // remove the first N elements from the buffer, where N is the number of updates batched in the latest send
      this.buffer = this.buffer.slice(this.outgoing);
      this.outgoing = 0;
    } else if (id !== undefined) {
      debugWarn(
        '[StateBridge:confirm] ❌ id MISMATCH — expected=%s, got=%s — triggering mismatch handler',
        this.outgoingId,
        id
      );
      // id was provided but doesn't match — signal that state is out of sync
      this.mismatchObserver?.();
    }
    return Promise.resolve();
  }

  onConfirm(observer: (sentAt: number, receivedAt: number) => void): this {
    this.confirmObserver = observer;
    return this;
  }

  onMismatch(observer: () => void): this {
    this.mismatchObserver = observer;
    return this;
  }
}
